import {
  type ChartAxisVisibleRange,
  ChartFrame,
  ChartLegend,
  type ChartPointAnnotation,
  type ChartSeries,
  type ChartShadingRegion,
  type ChartStripLine,
  dataVisColorTokens,
  LineChart,
} from "@assetwatch/charts";
import {
  Alert,
  Button,
  Divider,
  Group,
  Input,
  MultiSelect,
  Skeleton,
  Stack,
  Text,
  Tile,
} from "@assetwatch/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { mlApiErrorMessage } from "../../api/mlApi";
import type { MpGraph, MpGraphSeries, MpQueryCost } from "./api";
import {
  DEFAULT_TIME_RANGE_DAYS,
  MAX_GRAPHS,
  MAX_TIME_RANGE_DAYS,
  type MpChartHighlight,
  type MpGraphKey,
  markerPaint,
  markerShape,
  parseFocus,
  parseGraphs,
  parseHighlights,
  parseViewport,
  serializeFocus,
  serializeGraphs,
  toggleFocusLabel,
} from "./lib";
import { useMpGraphTypes, useMpVibrationGraphs } from "./queries";

const routeApi = getRouteApi("/_app/mp-graphs");

/**
 * Format a scan cost. Sub-hundredth-of-a-cent queries are common, and
 * `toFixed(4)` renders those as "$0.0000" — which reads as free rather than as
 * cheap — so anything below the displayable floor is shown as a bound.
 */
function formatUsd(usd: number): string {
  if (usd > 0 && usd < 0.0001) {
    return "<$0.0001";
  }
  return `$${usd.toFixed(4)}`;
}

// Query cost is worth surfacing rather than logging: these queries scan the data
// lake by the gigabyte, and a widened time range can multiply the scan without
// any visible change to the chart.
function QueryCostSummary({ cost }: { cost: MpQueryCost | null }) {
  if (!cost) {
    return null;
  }

  const parts = [
    // usd/mb_scanned are nullable — the backend couldn't determine the figure.
    // "unavailable" is the honest rendering; "$0" would claim the query was free.
    cost.usd === null ? "cost unavailable" : formatUsd(cost.usd),
    cost.mb_scanned === null
      ? "MB unavailable"
      : `${cost.mb_scanned.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        })} MB scanned`,
    cost.queries === null || cost.queries <= 1
      ? null
      : `${cost.queries} queries`,
    cost.rows === null ? null : `${cost.rows.toLocaleString()} rows`,
    cost.seconds === null ? null : `${cost.seconds.toFixed(2)}s`,
  ].filter((part): part is string => part !== null);

  return (
    // Titled rather than labelled inline: it's an estimate of data scanned, not
    // a bill, and that caveat doesn't deserve a line of chrome on every render.
    <Text size="sm" tone="subtle">
      <span title="Estimated cost of the data scanned by this query. Excludes compute; not a bill.">
        Scan estimate: {parts.join(" · ")}
      </span>
    </Text>
  );
}

/**
 * The chart-side id for one of a graph's series. The shared legend keys off the
 * axis prefix, which the API's `column` (e.g. "x_rms") already carries;
 * single-series graphs fall back to a synthetic id.
 */
function seriesIdFor(graph: MpGraph, serie: MpGraphSeries): string {
  return serie.column || `${serie.label.toLowerCase()}_${graph.key}`;
}

/**
 * Turn the API's columnar graph into chart series. `values` is index-aligned
 * with `timestamps`, and a `null` is a genuine gap the chart draws as a break.
 */
function buildSeries(graph: MpGraph): ChartSeries[] {
  const unit = graph.y_units || undefined;

  return graph.series.map((serie) => ({
    id: seriesIdFor(graph, serie),
    label: serie.label,
    valueUnit: unit,
    data: graph.timestamps.map((timestamp, index) => ({
      x: new Date(timestamp * 1000),
      y: serie.values[index] ?? null,
      // Explicit point id so a marker can address a point by its timestamp. The
      // chart would otherwise derive the key from the numeric x value, which
      // lands in the same place today but is an implementation detail.
      id: String(timestamp),
    })),
  }));
}

/**
 * Markers the API attached to this graph, as chart point annotations.
 *
 * Markers carry no `column` — the machine is running or it isn't — so each one is
 * drawn on every series of the graph. `pointKey` is the marker's timestamp, which
 * the API guarantees is one of the graph's `timestamps`; a marker that somehow
 * isn't on a plotted point is skipped by the chart rather than throwing.
 */
function buildPointAnnotations(graph: MpGraph): ChartPointAnnotation[] {
  const markers = graph.markers ?? [];

  if (markers.length === 0) {
    return [];
  }

  return graph.series.flatMap((serie) => {
    const seriesId = seriesIdFor(graph, serie);

    return markers.map(
      (marker): ChartPointAnnotation => ({
        seriesId,
        pointKey: String(marker.timestamp),
        marker: {
          type: markerShape(marker.shape),
          fill: markerPaint(marker.color),
          stroke: markerPaint(marker.color),
        },
      }),
    );
  });
}

/**
 * The distinct states present on a graph, in first-seen order. The chart can't
 * draw text at a marker, so the labels have to be listed beside it or the shapes
 * mean nothing.
 */
function markerLegendEntries(graph: MpGraph) {
  const seen = new Map<string, { label: string; note: string | null }>();

  for (const marker of graph.markers ?? []) {
    if (!seen.has(marker.label)) {
      seen.set(marker.label, {
        label: marker.label,
        note: marker.note ?? null,
      });
    }
  }

  return [...seen.values()];
}

function buildShadingRegions(
  graphKey: MpGraphKey,
  highlights: MpChartHighlight[],
): ChartShadingRegion[] {
  return highlights
    .filter(
      (highlight) => highlight.chart === graphKey && highlight.to !== undefined,
    )
    .map(
      (highlight, index): ChartShadingRegion => ({
        id: `highlight-${graphKey}-${index}`,
        axis: "x",
        from: new Date(highlight.from * 1000),
        to: new Date((highlight.to as number) * 1000),
        tone: "warning",
        opacity: "medium",
      }),
    );
}

function buildStripLines(
  graphKey: MpGraphKey,
  highlights: MpChartHighlight[],
): ChartStripLine[] {
  return highlights
    .filter((highlight) => highlight.chart === graphKey)
    .flatMap((highlight, index): ChartStripLine[] => {
      // Markers always draw a line; ranges are shaded, but a noted range also
      // gets a labeled line at its start so the note is visible on the chart.
      if (highlight.to !== undefined && !highlight.note) {
        return [];
      }

      return [
        {
          id: `${highlight.to === undefined ? "marker" : "note"}-${graphKey}-${index}`,
          axis: "x",
          value: new Date(highlight.from * 1000),
          colorToken: dataVisColorTokens.criticalYellow,
          label: highlight.note ?? "",
        },
      ];
    });
}

function MpGraphChart({
  graph,
  highlights,
  focusedLabels,
  visibleRange,
  onToggleSeries,
}: {
  graph: MpGraph;
  highlights: MpChartHighlight[];
  /** Lowercased labels to show, or undefined for every series. */
  focusedLabels: string[] | undefined;
  visibleRange: ChartAxisVisibleRange | undefined;
  onToggleSeries: (
    graphKey: MpGraphKey,
    label: string,
    allLabels: string[],
  ) => void;
}) {
  const series = buildSeries(graph);
  // Visibility is URL-driven and per chart: this graph's `focus` entry names the
  // series to show, and no entry shows all of them. Matched on label because
  // that's what the ML API's `graph_focus` speaks.
  const visibleSeriesIds = series
    .filter(
      (serie) =>
        focusedLabels === undefined ||
        focusedLabels.includes(serie.label.toLowerCase()),
    )
    .map((serie) => serie.id);

  const shadingRegions = buildShadingRegions(graph.key, highlights);
  const stripLines = buildStripLines(graph.key, highlights);
  const pointAnnotations = buildPointAnnotations(graph);
  const legendEntries = markerLegendEntries(graph);

  return (
    // The source is part of the identity of the chart: `rms_ts` and `rms_athena`
    // share a title but come off different grids, so the title alone is ambiguous
    // when both are plotted.
    <ChartFrame subtitle={graph.source || undefined} title={graph.title}>
      <Stack gap={8}>
        <LineChart
          emptyMessage="No readings in this range."
          height={320}
          pointAnnotations={
            pointAnnotations.length > 0 ? pointAnnotations : undefined
          }
          series={series}
          shadingRegions={
            shadingRegions.length > 0 ? shadingRegions : undefined
          }
          stripLines={stripLines.length > 0 ? stripLines : undefined}
          visibleSeriesIds={visibleSeriesIds}
          xAxis={{ title: "Time", visibleRange }}
          yAxis={{ title: graph.y_units || undefined }}
          zoom={{
            pan: true,
            select: true,
            syncGroup: "mp-graphs",
          }}
        />
        {series.length > 1 ? (
          <ChartLegend
            onToggleSeries={(seriesId) => {
              const clicked = series.find((serie) => serie.id === seriesId);
              if (clicked) {
                onToggleSeries(
                  graph.key,
                  clicked.label,
                  series.map((serie) => serie.label),
                );
              }
            }}
            series={series}
            visibleSeriesIds={visibleSeriesIds}
          />
        ) : null}

        {legendEntries.length > 0 ? (
          <Text size="sm" tone="subtle">
            Markers:{" "}
            {legendEntries
              .map((entry) =>
                entry.note ? `${entry.label} (${entry.note})` : entry.label,
              )
              .join(" · ")}
          </Text>
        ) : null}

        {graph.markers_truncated ? (
          // Said out loud rather than swallowed: a partial marker set looks
          // exactly like a machine that changed state less often than it did.
          <Text size="sm" tone="subtle">
            Only the most recent state changes are shown — there were more than
            the endpoint returns. Narrow the day range to see the rest.
          </Text>
        ) : null}
      </Stack>
    </ChartFrame>
  );
}

export function MpGraphsScreen() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const selectedGraphs = parseGraphs(search.graph);
  const highlights = parseHighlights(search.highlight);
  const focus = parseFocus(search.focus);
  const timeRangeDays = search.days ?? DEFAULT_TIME_RANGE_DAYS;
  const hasSelection = Boolean(search.mp && selectedGraphs.length > 0);

  // The catalog drives the picker. Fetched rather than hardcoded so graphs added
  // on the backend show up without a frontend change.
  const graphTypes = useMpGraphTypes();
  const graphOptions = (graphTypes.data ?? []).map((type) => ({
    value: type.key,
    label: type.source ? `${type.title} · ${type.source}` : type.title,
  }));

  // Titles for keys that aren't currently plotted — a note in the URL can name a
  // graph that isn't selected, and the catalog may not have loaded yet.
  const graphTitle = (key: MpGraphKey) => {
    const type = graphTypes.data?.find((candidate) => candidate.key === key);
    if (!type) {
      return key;
    }
    return type.source ? `${type.title} · ${type.source}` : type.title;
  };

  // Form state is seeded from the URL so a shared link pre-fills the controls.
  const [mp, setMp] = useState(search.mp ?? "");
  const [graphs, setGraphs] = useState<MpGraphKey[]>(selectedGraphs);
  const [days, setDays] = useState(
    String(search.days ?? DEFAULT_TIME_RANGE_DAYS),
  );

  const canPlot = mp.trim().length > 0 && graphs.length > 0;

  // Bumping this key remounts every chart, which is the simplest reliable way
  // to reset each graph's zoom range back to full extents.
  const [resetKey, setResetKey] = useState(0);

  // One request serves every chart on the page: the backend batches the
  // requested keys by source, so this is cheaper than a call per graph.
  const graphData = useMpVibrationGraphs({
    monitoringPointId: hasSelection ? search.mp : undefined,
    timeRangeDays,
    graphs: selectedGraphs,
  });

  // Graphs come back in catalog order, so re-order them into the order the user
  // picked rather than matching positionally.
  const graphsByKey = new Map(
    (graphData.data?.graphs ?? []).map((graph) => [graph.key, graph]),
  );
  const plottedGraphs = graphData.data
    ? selectedGraphs.map((key) => graphsByKey.get(key))
    : undefined;

  // The window to render, in Unix seconds — the same unit the charts report and
  // the URL carries. It comes from the URL alone: a shared or drilled-into link
  // opens on exactly the window it names, and with no `x_start`/`x_stop` the
  // charts show the full fetched extent. Panning and zooming stay inside the
  // chart and never write back here, which is what lets "Reset view" return to
  // this window.
  const urlViewport = parseViewport(search.x_start, search.x_stop);
  const visibleRange: ChartAxisVisibleRange | undefined = urlViewport
    ? {
        min: new Date(urlViewport.min * 1000),
        max: new Date(urlViewport.max * 1000),
      }
    : undefined;

  function handlePlot() {
    if (!canPlot) {
      return;
    }

    const parsedDays = Number.parseInt(days, 10);
    // Clamped, not passed through: the search schema caps `days` at
    // MAX_TIME_RANGE_DAYS and drops anything larger, which would quietly fall
    // back to DEFAULT_TIME_RANGE_DAYS instead of honoring what was asked for.
    // The field is capped as you type too; this also covers a value seeded from
    // a hand-edited URL.
    const appliedDays =
      Number.isFinite(parsedDays) && parsedDays > 0
        ? Math.min(parsedDays, MAX_TIME_RANGE_DAYS)
        : undefined;

    if (appliedDays !== undefined && appliedDays !== parsedDays) {
      setDays(String(appliedDays));
    }

    navigate({
      search: {
        mp: mp.trim(),
        graph: serializeGraphs(graphs),
        days: appliedDays,
        highlight: search.highlight,
        focus: search.focus,
        x_start: undefined,
        x_stop: undefined,
      },
    });
  }

  /**
   * Return the charts to the state the URL describes.
   *
   * The URL is the source of truth for the viewport, the highlights and the
   * focused series, and panning/zooming never writes to it — so everything worth
   * restoring is already there and nothing needs to be navigated. Remounting the
   * charts discards their internal zoom, which re-applies `visibleRange` (the
   * URL's window, or full extents when it names none).
   */
  function handleReset() {
    setResetKey((key) => key + 1);
  }

  function handleToggleSeries(
    graphKey: MpGraphKey,
    label: string,
    allLabels: string[],
  ) {
    const next = toggleFocusLabel(
      parseFocus(search.focus),
      graphKey,
      allLabels,
      label,
    );
    navigate({ search: { ...search, focus: serializeFocus(next) } });
  }

  return (
    <Stack gap={24}>
      <Tile padding="comfortable" radius="md" tone="muted">
        <Group align="end" gap={16} wrap="wrap">
          <div className="w-64">
            <Input
              label="Monitoring Point ID"
              name="mp"
              onChange={(event) => setMp(event.target.value)}
              placeholder="external_monitoringpoint_id"
              reserveSubtextSpace={false}
              value={mp}
            />
          </div>
          <div className="w-72">
            <MultiSelect
              disabled={graphOptions.length === 0}
              error={
                graphTypes.isError
                  ? (mlApiErrorMessage(graphTypes.error) ??
                    "Couldn't load the graph list.")
                  : undefined
              }
              label="Graphs"
              // Capped here rather than only on read: parseGraphs truncates the
              // URL to MAX_GRAPHS, so letting a sixth be picked would drop it
              // silently after "Plot graphs".
              onChange={(value) => setGraphs(value.slice(0, MAX_GRAPHS))}
              options={graphOptions}
              placeholder={
                graphTypes.isPending ? "Loading graphs…" : "Select graphs"
              }
              reserveSubtextSpace={false}
              value={graphs}
            />
          </div>
          <div className="w-24">
            <Input
              inputMode="numeric"
              label="Days"
              max={MAX_TIME_RANGE_DAYS}
              min={1}
              name="days"
              // Capped as it's typed so the field can never hold a range the
              // search schema would discard. Partial input ("" while editing) is
              // left alone.
              onChange={(event) => {
                const raw = event.target.value;
                const parsed = Number.parseInt(raw, 10);
                setDays(
                  Number.isFinite(parsed) && parsed > MAX_TIME_RANGE_DAYS
                    ? String(MAX_TIME_RANGE_DAYS)
                    : raw,
                );
              }}
              reserveSubtextSpace={false}
              value={days}
            />
          </div>
          <Button disabled={!canPlot} onClick={handlePlot} type="button">
            Plot graphs
          </Button>
        </Group>
      </Tile>

      <Divider emphasis="high" />

      {!hasSelection ? (
        <Text tone="subtle">
          Enter a monitoring point ID, choose one or more graphs, then select
          “Plot graphs”.
        </Text>
      ) : graphData.isError ? (
        <Alert
          subtext={
            mlApiErrorMessage(graphData.error) ??
            "Check the monitoring point ID is a well-formed GUID and try again. A 403 means your Cognito identity isn't on the ML API's tester allowlist yet."
          }
          title="Couldn't load graphs"
          tone="negative"
        />
      ) : (
        <Stack gap={16}>
          {graphData.data ? (
            <Group align="center" gap={16} justify="between" wrap="wrap">
              <QueryCostSummary cost={graphData.data.cost} />
              <Group gap={8} justify="end" wrap="wrap">
                <Button
                  onClick={handleReset}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Reset view
                </Button>
              </Group>
            </Group>
          ) : null}

          <div
            className="grid gap-(--spacing-16)"
            style={{
              gridTemplateColumns: `repeat(${selectedGraphs.length}, minmax(0, 1fr))`,
            }}
          >
            {selectedGraphs.map((key, index) => {
              // `plottedGraphs` is undefined until the response lands; an entry
              // is undefined when the response didn't include a requested key
              // (a stale URL naming a graph the catalog has dropped).
              const graph = plottedGraphs?.[index];

              if (plottedGraphs && !graph) {
                return (
                  <ChartFrame key={key} title={graphTitle(key)}>
                    <Text tone="subtle">
                      The ML API didn’t return this graph. It may no longer be
                      in the catalog.
                    </Text>
                  </ChartFrame>
                );
              }

              return graph ? (
                <div key={`${key}-${resetKey}`}>
                  <MpGraphChart
                    graph={graph}
                    highlights={highlights}
                    focusedLabels={focus.get(graph.key)}
                    onToggleSeries={handleToggleSeries}
                    visibleRange={visibleRange}
                  />
                </div>
              ) : (
                <ChartFrame key={key} title={graphTitle(key)}>
                  <Skeleton height={320} radius="md" width="100%" />
                </ChartFrame>
              );
            })}
          </div>
        </Stack>
      )}
    </Stack>
  );
}
